/**
 * The geofence reminder on the device (spec 12.4, T-188, ADR-0167): which regions the device
 * watches and when a reminder is about due work. Pure functions; the platform keeps the result.
 */

/** A region the device watches: an own establishment with its radius. */
export interface ReminderRegion {
  /** The establishment. */
  readonly id: string;
  /** Latitude in degrees. */
  readonly latitude: number;
  /** Longitude in degrees. */
  readonly longitude: number;
  /** Radius in metres. */
  readonly radiusMeters: number;
}

/** A period in which a working day or a shift is due, in milliseconds since the epoch. */
export interface ReminderWindow {
  /** Inclusive start. */
  readonly start: number;
  /** Exclusive end. */
  readonly end: number;
}

/**
 * What the device keeps for the reminder. It holds no position of the device and never leaves it.
 */
export interface GeofenceSetup {
  /** Regions of the own establishments; empty while no region reminder is armed. */
  readonly regions: readonly ReminderRegion[];
  /** Periods in which a working day or a shift is due. */
  readonly windows: readonly ReminderWindow[];
  /** Starts of own published shifts (reminder at the shift start without regions). */
  readonly shiftStarts: readonly number[];
  /** Whether the person is clocked in as far as the app knows. */
  readonly clockedIn: boolean;
}

/** The answer of `GET /me/geofence-reminder` as far as the device needs it. */
export interface GeofenceAnswer {
  /** Regions of the own establishments. */
  readonly regions: readonly {
    readonly establishmentId: string;
    readonly latitude: number;
    readonly longitude: number;
    readonly radiusMeters: number;
  }[];
  /** Periods as ISO 8601 instants. */
  readonly windows: readonly { readonly start: string; readonly end: string }[];
  /** Shift starts as ISO 8601 instants. */
  readonly shiftStarts: readonly string[];
}

/**
 * How far ahead a region reminder that fires only once (iOS: the system shows it without waking
 * the app) is armed: a period that begins within it, so a normal weekend is bridged; after longer
 * free days the app arms it again when it is opened.
 */
export const oneShotArmingHorizonMs = 72 * 60 * 60 * 1000;

/**
 * Returns whether a region reminder that fires only once is armed now: only while the person is not
 * clocked in and work is due now or within {@link oneShotArmingHorizonMs}.
 *
 * @param windows - The periods.
 * @param clockedIn - Whether the person is clocked in.
 * @param nowMs - Now.
 * @returns `true` to register the regions.
 */
export function armsOneShotRegions(
  windows: readonly ReminderWindow[],
  clockedIn: boolean,
  nowMs: number,
): boolean {
  const horizon = nowMs + oneShotArmingHorizonMs;
  const armed =
    !clockedIn && windows.some((window) => window.end > nowMs && window.start <= horizon);

  return armed;
}

/**
 * Builds what the device keeps from the server's answer and the clock state.
 *
 * @param answer - The answer of the server.
 * @param clockedIn - Whether the person is clocked in.
 * @param nowMs - Now.
 * @param oneShotRegions - Whether a region reminds only once per arming (iOS).
 * @returns The setup.
 */
export function geofenceSetupOf(
  answer: GeofenceAnswer,
  clockedIn: boolean,
  nowMs: number,
  oneShotRegions: boolean,
): GeofenceSetup {
  const windows = answer.windows
    .map((window) => ({ start: Date.parse(window.start), end: Date.parse(window.end) }))
    .filter((window) => window.end > nowMs);
  const shiftStarts = answer.shiftStarts
    .map((start) => Date.parse(start))
    .filter((start) => start > nowMs);
  const armed = !oneShotRegions || armsOneShotRegions(windows, clockedIn, nowMs);
  const regions = armed
    ? answer.regions.map((region) => ({
        id: region.establishmentId,
        latitude: region.latitude,
        longitude: region.longitude,
        radiusMeters: region.radiusMeters,
      }))
    : [];
  const setup: GeofenceSetup = { regions, windows, shiftStarts, clockedIn };

  return setup;
}
